import { ApolloClient } from '@apollo/client';
import { VEHICLE_LIST_ITEM_QUERY } from './vehicles.gql';

interface VehicleTypeSummary {
	name?: string | null;
}

interface VehicleCustomerSummary {
	displayName?: string | null;
}

export interface VehicleListRecord {
	vehicleId: string;
	plateNumber?: string | null;
	vehicleType?: VehicleTypeSummary | null;
	customer?: VehicleCustomerSummary | null;
	make?: string | null;
	model?: string | null;
	isActive: boolean;
}

interface VehicleListItemResponse {
	vehicle?: VehicleListRecord | null;
}

export interface SelectVehicleAfterSaveOptions {
	vehicleId: string;
	client: ApolloClient<any>;
	currentVehicles: VehicleListRecord[];
	onDataUpdate: (updatedVehicles: VehicleListRecord[]) => void;
	onVehicleSelect: (vehicle: VehicleListRecord) => void;
}

/**
 * Fetches the latest vehicle data from the server and updates the list,
 * then selects and highlights the vehicle in the grid and details panel.
 *
 * @param options Configuration options for selecting the vehicle
 * @returns Promise that resolves when the operation is complete
 */
export async function selectVehicleAfterSave(
	options: SelectVehicleAfterSaveOptions
): Promise<void> {
	const { vehicleId, client, currentVehicles, onDataUpdate, onVehicleSelect } = options;

	try {
		// Step 1: Fetch the latest vehicle data from the server
		const { data } = await client.query<VehicleListItemResponse>({
			query: VEHICLE_LIST_ITEM_QUERY,
			variables: { id: vehicleId },
			fetchPolicy: 'network-only' // Always fetch fresh data from server
		});

		const fetchedVehicle = data?.vehicle;

		if (!fetchedVehicle) {
			console.warn(`Vehicle with ID ${vehicleId} not found after save`);
			return;
		}

		// Step 2: Check if vehicle exists in current list
		const existingVehicleIndex = currentVehicles.findIndex(
			v => v.vehicleId === vehicleId
		);

		let updatedVehicles: VehicleListRecord[];

		if (existingVehicleIndex >= 0) {
			// Vehicle exists - replace with updated data
			updatedVehicles = [...currentVehicles];
			updatedVehicles[existingVehicleIndex] = fetchedVehicle;
		} else {
			// New vehicle - append to the list
			updatedVehicles = [...currentVehicles, fetchedVehicle];
		}

		// Step 3: Update the list data
		onDataUpdate(updatedVehicles);

		// Step 4: Select the vehicle (both in grid and details panel)
		// Use setTimeout to ensure the grid has been updated before selection
		setTimeout(() => {
			onVehicleSelect(fetchedVehicle);
		}, 50);
	} catch (error) {
		console.error('Error selecting vehicle after save:', error);
		throw error;
	}
}
